import { memo, useCallback, useMemo, useState } from "react";
import { MainContentPaddingTypes, NewModal, Overlay } from "../BaseComponents";
import { useSystem } from "../../Services/Agrisys_GqlDataCaller/SystemCalls/useSystem";
import { useAppSelector } from "../../Store";
import { Graph_QL } from "../../Services";
import { colors, css, glyphs } from "../../Styles";
import { ButtonTypes } from "../../Definitions";
import ATTENTION_BAR from "./attentionBar";
import { useAttentionLog } from "../../Services/Agrisys_GqlDataCaller/AttentionLogCalls/useAttentionLog";
import { AttentionLog } from "../../Services/Agrisys_GqlDataCaller/AttentionLogCalls/GQLCalls/getAttentionLogs";
import { useTranslation } from "react-i18next";
import NewButton, { NewButtonList } from "../BaseComponents/newButton";

// #region | AttentionLogList
const AttentionLogList = memo(({logData, selectedLogIds, onCheck, onHeaderCheck, editable, color } : {
  logData: AttentionLog[],
  selectedLogIds: number[],
  onHeaderCheck: () => void;
  onCheck: (logId: number) => void,
  editable: boolean
  color: colors
}) => (
  <>
    <ATTENTION_BAR headerBar onCheck={onHeaderCheck} isChecked={selectedLogIds.length === logData.length && logData.length > 0} selectable={editable} borderRadius={{borderRadius: logData.length === 0 ? "10px" : "10px 10px 0px 0px"}}/> 
    {logData.map((log, index) => {
      const logId = log.attention_log_id;
      const isSelected = selectedLogIds.includes(logId);

      return (
        <ATTENTION_BAR
          key={log.attention_log_id}
          selectable={editable}
          attentionColor={color}
          attention={log}
          borderRadius={{ borderRadius: index === logData.length - 1 ? "0px 0px 10px 10px" : "0px" }}
          isChecked={isSelected}
          onCheck={() => onCheck(logId)}
        />
      );
    })}
  </>
));
// #endregion

interface AttentionModal_Props{
  modalState: boolean;
  onClose: () => void;
  systemId: number | string
  attentionview?: "ALERTS" | "WARNINGS";
}
/**
 * AttentionModal
 * @param modalState - Controls whether the modal is visible
 * @param onClick - Function to call when the modal should be closed
 * @param systemId - The ID of the system for which attention logs are shown
 * @param attentionview - Optional view type to filter logs by level (alerts or warnings) 
 * @returns A React component rendering the attention log modal
 */
export default function AttentionModal({ modalState, onClose, systemId, attentionview }: AttentionModal_Props) {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [currentAttentionView, setCurrentAttentionView] = useState<"ALERTS" | "WARNINGS">(attentionview ?? "ALERTS");
  const [selectedLogIds, setSelectedLogIds] = useState<number[]>([]);

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({ domain: farm })), [farm]);
  
  const { data: systemsState } = useSystem(connection); 
  const { data: attentionLogState, maintainEntries: dismissAttentionLog} = useAttentionLog(connection);

  // System Name
  const systemName = useMemo(() => systemsState[domain].data.find((system) => system.system_id === Number(systemId))?.name, [systemsState, domain, systemId])

  // Log Data
  const logData = useMemo(() => {
    const logs = attentionLogState?.[domain]?.data ?? [];
  
    return logs.filter(
      (log) =>
        log.fk_systems === +systemId &&
        log.dismissed !== true &&
        (currentAttentionView === "ALERTS" ? log.level === 0 : log.level === 1)
    ).sort((a, b) => (b.timestamp) - (a.timestamp));
  }, [attentionLogState, currentAttentionView, domain, systemId]);

  // Handle check on headerBar
  const toggleLogSelection = useCallback(() => {
    if (selectedLogIds.length === logData.length) {
      setSelectedLogIds([]);
    } else {
      setSelectedLogIds(logData.map((log) => log.attention_log_id));
    }
  }, [logData, selectedLogIds]);
  
  // Handle check on single attention
  const singleCheck = useCallback((logId: number) => {
    setSelectedLogIds((prev) =>
      prev.includes(logId) ? prev.filter((id) => id !== logId) : [...prev, logId]
    );
  }, [])

  // Translations
  const translations = useMemo(() => ({
    alerts: t("systemControls.general.alerts", { defaultValue: "alerts" }),
    warnings: t("systemControls.general.warnings", { defaultValue: "warnings" }),
    delete: t("systemControls.general.delete", { defaultValue: "delete" }),
    penHeaders: t("locationFeederPages.sections.penValueHeaders", { defaultValue: "Hold the mouse on a value to see column type" }),
    save: t("buttons.save", { defaultValue: "save" }),
    cancel: t("buttons.cancel", { defaultValue: "cancel" })
  }), [t]);

  return (
    <NewModal
      modalState={modalState}
      header={currentAttentionView === "ALERTS" ? translations.alerts : translations.warnings}
      subHeader={systemName}
      overrides={{
        backgroundColor: colors.greyLightTertiary,
        mainContentPadding: MainContentPaddingTypes.primary,
        mainContentScroll: true
      }}
      tabButtons={{
        contents: [translations.alerts, translations.warnings],
        active: currentAttentionView === "ALERTS" ? 0 : 1,
        disabled: currentAttentionView === "ALERTS" ? 0 : 1,
        onClick: (i) => {
          if (i === 0) setCurrentAttentionView("ALERTS")
          if (i === 1) setCurrentAttentionView("WARNINGS")
        },
        startEnhancers: [
          <div style={{ position: "absolute", top: "1px", left: "1px", bottom: "40px", right: "1px", backgroundColor: colors.systemRed, borderRadius: "8px 8px 0px 0px" }} />,
          <div style={{ position: "absolute", top: "1px", left: "1px", bottom: "40px", right: "1px", backgroundColor: colors.systemYellow,  borderRadius: "8px 8px 0px 0px" }} />
        ]
        // startEnhancers: [glyphs.Attention({fillColor: colors.systemRed, size: "25px"}), glyphs.Attention({fillColor: colors.systemYellow, size: "25px"})]
      }}
      predefinedButtons={{
        onClose: () => onClose()
      }}
      disabledTop={editMode}
      mainContent={<>
        <div style={{ display: "flex", justifyContent: editMode ? "end" : "space-between", alignItems: "end", width: "100%", padding: "0px 20px", boxSizing: "border-box", zIndex: editMode ? "9999" : undefined, position: editMode ? "relative" : undefined }}>
          {editMode ? <></> : (
            <p style={{ ...css.penHeaders }}>
              {translations.penHeaders}
            </p>
          )}
          {editMode ? (
            <NewButtonList
              contents={[translations.save, translations.cancel, translations.delete]} 
              onClick={(i => {
                if (i === 0) { if (dismissAttentionLog) { dismissAttentionLog({ data: selectedLogIds }) }; setEditMode(false)}
                else if (i === 1) {setEditMode(false)}
              })}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[
                glyphs.Check({fillColor: colors.greyDarkPrimary}),
                glyphs.Close({fillColor: colors.greyDarkPrimary}),
                glyphs.Delete({fillColor: colors.greyDarkPrimary})
              ]}
              disabled={[false, false, true, true, true]}
              overrides={(index) => ({
                baseStyles: {
                  backgroundColor: index === 2 ? colors.greyLightSecondary : colors.greyLightPrimary
                },
                hoverStyles: {
                  backgroundColor: colors.greyLightSecondary
                },
                contentStyles: {
                  color: colors.greyDarkPrimary
                }
              })}
            />
          ) : (
            <NewButton
              content={translations.delete} 
              onClick={() => setEditMode(true)}
              buttonType={ButtonTypes.tableButton}
              startEnhancer={glyphs.Delete({fillColor: colors.greyDarkPrimary})}
              overrides={{
                baseStyles: { backgroundColor: colors.greyLightPrimary },
                hoverStyles: { backgroundColor: colors.greyLightSecondary },
                contentStyles: { color: colors.greyDarkPrimary }
              }}
            />
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", zIndex: editMode ? "9999" : undefined, }}>
          <AttentionLogList 
            logData={logData} 
            selectedLogIds={selectedLogIds}
            onHeaderCheck={toggleLogSelection}
            onCheck={singleCheck} 
            editable={editMode} 
            color={currentAttentionView === "ALERTS" ? colors.systemRed : colors.systemYellow} 
          />
        </div>
        
      </>}
    />
  ) 
}